import { useState } from 'react';
import { useToast } from '@shared/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { OrdersApiAdapter, ExportOrdersPayload } from '../adapters/OrdersApiAdapter';
import { FilterConditionOutput } from '@components/ui/AdvancedFilter';

export const useExportOrders = () => {
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation('ordersModule');

  const exportOrders = async (
    filterConditions: FilterConditionOutput[],
    search?: string,
    statusId?: number,
    sorting?: { field: string; direction: 'asc' | 'desc' },
    visibleColumns?: string[],
  ) => {
    setIsExporting(true);

    try {
      const httpClient = createHttpClient(apiClientWH);
      const ordersAdapter = new OrdersApiAdapter(httpClient);

      // Construir el payload de exportación
      const payload: ExportOrdersPayload = {};

      // Agregar término de búsqueda si existe
      if (search) {
        payload.search = search;
      }

      // Agregar filtros avanzados si existen
      if (filterConditions.length > 0) {
        payload.advanced_filters = {
          filters: filterConditions.map((condition) => {
            const requiresArray = ['in', 'not_in'].includes(condition.action);
            const isNullOperator = ['is_null', 'is_not_null'].includes(condition.action);

            const value = isNullOperator ? undefined : requiresArray ? [condition.value] : condition.value;

            return {
              column: condition.column,
              operator: condition.action,
              ...(value !== undefined && { value: value }),
            };
          }),
          logic: 'AND',
        };
      }

      // Agregar filtro de estado si existe
      // Cuando el estado es 4 (en preparación), incluir también el estado 5
      if (statusId) {
        payload.status_ids = statusId === 4 ? [4, 5] : statusId;
      }

      // Agregar ordenamiento; si no existe, usar el mismo default que la lista (order_created_at, desc)
      const sortField = sorting?.field || 'order_created_at';
      const sortDirection = sorting?.direction || 'desc';
      payload.sort_by = sortField;
      payload.sort_direction = sortDirection as 'asc' | 'desc';

      // Log de depuración

      console.log('🔍 useExportOrders - Built payload:', payload, 'visibleColumns:', visibleColumns);

      const response = await ordersAdapter.exportOrders(payload, visibleColumns);

      if (response.url_file) {
        // Redirigir al usuario al archivo Excel
        window.open(response.url_file, '_blank');

        toast({
          title: t('export.success.title'),
          description: t('export.success.description'),
          variant: 'default',
        });
      } else {
        throw new Error('No se recibió URL del archivo');
      }
    } catch (error: any) {
      toast({
        title: t('export.error.title'),
        description: error.message || t('export.error.unexpected'),
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return {
    exportOrders,
    isExporting,
  };
};
